<template>
    <div>
        <div class="sm:w-full md:w-1/2 2xl:w-1/3 bg-white shadow sm:rounded-lg mb-4">
            <!-- Banner to notify Iso Facility Environment is not validated -->
            <div    v-if="! isoFacilityEnvironmentValidated"
                    class="mb-2">
                <banner>
                    <template   #title>
                        Facility Environment Status
                    </template>
                    <template   #content>
                        The facility environment status is not valid for today. Please update the facility environment status.
                        You will not be able to register calibrations until the facility environment status is
                        <undecorated-link   :href="isoFacilityValidationUrl">validated</undecorated-link>.
                    </template>
                </banner>
            </div>

            <div class="px-4 py-5 sm:p-6">
                <h5>Reference Probe Values</h5>
                <!-- Company Reference Values. -->
                <floating-input     v-for="(reference, name) in references"
                                    :id="name + '_value'"
                                    v-model:value="tempReferenceData[name]"
                                    :label="getReferenceLabel(reference)"></floating-input>

                <!-- Button to update reference data that is sent to the probe-calibration-meters
                     causing an extra check to see if the point is ready for calibration. -->
                <sonicu-button @click="updateReferenceValue">Update Reference(s)</sonicu-button>
            </div>
        </div>

        <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <probe-calibration-meter
                v-for="meter in data['meters']"
                :key="meter.SerialNumber"
                :meter="meter"
                :approved-by-index="approvedByIndex"
                :performed-by-index="performedByIndex"
                :is-nist="isNist"
                :probe-type="isSSeries ? 'S-Series' : probeType"
                :ult-testing-method="ultTestingMethod"
                :probe-type-information="data.probeType"
                :device-series="deviceSeries"
                :reference-probe="getReferenceProbeForMeter(meter)"
                :calibration-equipment="calibrationEquipment"
                :iso-facility-environment-validated="isoFacilityEnvironmentValidated"
                :bypass-s-series-bounds="bypassSSeriesBounds"
                :reference-value="getReferenceValueForMeter(meter)">
            </probe-calibration-meter>
        </ul>
    </div>
</template>

<script>
import ProbeCalibrationMeter from "@/components/ProbeCalibration/ProbeCalibrationMeter";
import FeatureLevelPermission from "@/components/DevelopmentTesting/FeatureLevelPermission.vue";
import FloatingInput from "@/components/elements/FloatingInput.vue";
import SonicuButton from "@/components/elements/buttons/SonicuButton.vue";
import _ from 'lodash'
import Banner from "../Banners/Banner.vue";
import axios from "axios";
import {iconNamesMixin} from "../../mixins/IconNamesMixin";
import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import UndecoratedLink from "../EnterpriseDashboard/undecorated-link.vue";

export default {
    name: "ProbeCalibrationCreate",
    components: {UndecoratedLink, Banner, SonicuButton, FloatingInput, FeatureLevelPermission, ProbeCalibrationMeter},
    mixins: [SonicuCoreMixin, iconNamesMixin],
    props: {
        deviceSeries: {
            type: String,
            required: true
        },
        data: {
            type: Object,
            required: true
        },
        probeType: {
            type: String,
            required: true
        },
        calibrationEquipment: {
            type: Object,
            required: true
        },
        ultTestingMethod: {
            type: String,
            required: true
        },
        performedByIndex: {
            type: String,
            required: true
        },
        approvedByIndex: {
            type: String,
            required: true
        },
        bypassSSeriesBounds: {
            type: Boolean,
            required: false,
            default: false
        },
    },
    data() {
        return {
            isNist: true,

            // Reference Probes
            references: [],

            // Reference Data. i.e.) Probe => value
            tempReferenceData: {},
            referenceData: {},

            // Dictionary for method to determine which reference probe to use based on device series
            getReferenceProbeDictionary: {
                'sonishield': 'getReferenceProbeForMeterSoniShield',
                's-series-temperature': 'getReferenceProbeForMeterSSeriesTemperature',
                's-series-ht': 'getReferenceProbeForMeterSSeriesHt',
                's-series-ht-co2': 'getReferenceProbeForMeterSSeriesHtCo2',
                's-series-rope-ht': 'getReferenceProbeForMeterSSeriesHt',
                's-series-light': 'getReferenceProbeForMeterSSeriesLight',
            },
            isoFacilityEnvironmentValidated: false,
        };
    },
    created() {
        // Created, so no attempt to render is made before these are set.
        if (_.has(this.data, 'references')) {
            this.references = this.data.references
        }

        // Mark data on is-nist if it exists
        if (_.has(this.data, 'is_nist')) {
            this.isNist = this.data.is_nist
        }

        _.each(this.references, (reference, name) => {
            this.tempReferenceData[name] = ''
            this.referenceData[name] = ''
        })
    },
    mounted() {
        this.validateIsoFacilityEnvironment();
    },
    methods: {
        getReferenceProbeForMeter(meter) {
            if (_.isNil(meter)) {
                return null
            }

            // Use a dictionary to determine which method to use to get the reference probe based on device series
            let methodName = this.getReferenceProbeDictionary[this.deviceSeries]
            let referenceProbeName = this[methodName](meter)
            return this.$data.references[referenceProbeName]
        },
        getReferenceValueForMeter(meter) {
            if (_.isNil(meter)) {
                return null
            }

            let methodName = this.getReferenceProbeDictionary[this.deviceSeries]
            let referenceProbeName = this[methodName](meter)
            return this.referenceData[referenceProbeName]
        },
        getReferenceProbeForMeterSoniShield(meter) {
            return 'referenceProbe'
        },
        getReferenceProbeForMeterSSeriesTemperature(meter) {
            return 'tempReference'
        },
        getReferenceProbeForMeterSSeriesHt(meter) {
            return meter.probeType === 'Temperature' ? 'tempReference' : 'humidityReference'
        },
        getReferenceProbeForMeterSSeriesHtCo2(meter) {
            switch (meter.probeType) {
                case 'Temperature':
                    return 'tempReference'
                case 'Humidity':
                    return 'humidityReference'
                case 'CO2':
                    return 'co2Reference'
            }
        },
        getReferenceProbeForMeterSSeriesLight(meter) {
            return 'lightReference'
        },
        updateReferenceValue() {
            _.each(this.tempReferenceData, (value, name) => {
                this.referenceData[name] = value
            })
        },
        validateIsoFacilityEnvironment() {
            axios.get(this.company.buildApiInternalRoute('facility-environment-status-valid'))
                .then(response => {
                    this.isoFacilityEnvironmentValidated = response.data.validated;
                })
                .catch(error => {
                    console.error(error);
                })
                .then(() => {
                    // If the facility environment is not validated, check again in 5 seconds
                    if (!this.isoFacilityEnvironmentValidated) {
                        setTimeout(() => {
                            this.validateIsoFacilityEnvironment()
                        }, 5000)
                    }
                });
        },
        getReferenceLabel(reference) {
            let isLiquidNitrogen = reference['name'].toLowerCase().includes('liquid nitrogen')

            return `${reference['name']}${isLiquidNitrogen ? ' (-196.XX)' : ''}`
        }
    },
    computed: {
        isSSeries() {
            return this.deviceSeries !== 'sonishield'
        },
        isoFacilityValidationUrl() {
            return '/nova/resources/facility-environment-sensor-status-resources'
        },
    },
    watch: {
        isoFacilityEnvironmentValidated(val, old) {
            console.log(`Iso Facility Environment Validated: ${val}`)
            if (val !== old) {
                this.$forceUpdate();
            }
        }
    }
}
</script>

<style scoped>

</style>